import { QuickstartTheme } from '../../types/theme';
import { ICON_DROP_ARTBOARD_WIDTH, ICON_DROP_PX } from './iconDropScale';

/** Default game mark on the 412 artboard — larger than the earlier 136 slot. */
export const ICON_DROP_DEFAULT_GAME_LOGO_PX = 160;

/**
 * Studio max-height as a share of the game mark's layout/slot height
 * (artboard / theme game height, default 160) — not trimmed ink and not
 * game width. 30% of 160 is 48px: Payday comes down from ~56, STF stays near 50.
 */
export const ICON_DROP_DEFAULT_STUDIO_RATIO = 0.3;

/**
 * Typical Icon Drop game-mark aspect when we only have a height.
 * Used for a generous studio max-width so height stays the constraint.
 */
export const ICON_DROP_ASSUMED_GAME_ASPECT = 1.75;

/**
 * Studio max-width as a share of the estimated game mark width.
 * Jagex is ~7.5:1; without this it matches the game wordmark. Half keeps
 * OVERKILL height-limited (~2.7:1 needs ~131px of a 140px cap).
 */
export const ICON_DROP_STUDIO_WIDTH_RATIO = 0.5;

/**
 * Widest a studio mark may run versus its height cap.
 * OVERKILL is ~2.7:1 and stays height-limited; Jagex is ~7.5:1 and must
 * not stretch to the game wordmark's width.
 */
export const ICON_DROP_STUDIO_MAX_ASPECT = 3;

export interface IconDropLogoSizes {
  studioHeight: number;
  gameHeight: number;
  /** Stage-width fraction. Studio max width is the estimated game mark, not a % of it. */
  studioMaxWidthFraction: number;
  gameMaxWidthFraction: number;
}

export const estimateGameLogoWidthPx = (gameHeight: number): number =>
  Math.min(gameHeight * ICON_DROP_ASSUMED_GAME_ASPECT, ICON_DROP_PX.gameLogoMaxWidth * ICON_DROP_ARTBOARD_WIDTH);

/**
 * Contain box for the studio mark. Height is 30% of the layout game slot.
 * Width is half the game mark so a Jagex-style wordmark stays secondary.
 */
export const resolveStudioContainBox = (
  gameWidth: number,
  layoutGameHeight: number,
): { maxWidth: number; maxHeight: number } => {
  const maxHeight = layoutGameHeight * ICON_DROP_DEFAULT_STUDIO_RATIO;
  return {
    maxWidth: Math.min(gameWidth * ICON_DROP_STUDIO_WIDTH_RATIO, maxHeight * ICON_DROP_STUDIO_MAX_ASPECT),
    maxHeight,
  };
};

/** Studio height never exceeds 30% of the layout game slot, even if an old override is larger. */
export const resolveStudioHeightCap = (gameHeight: number, studioHeightOverride?: number): number => {
  const studioHeightCap = gameHeight * ICON_DROP_DEFAULT_STUDIO_RATIO;
  return Math.min(studioHeightOverride ?? studioHeightCap, studioHeightCap);
};

export const resolveIconDropLogoSizes = (
  theme: Pick<QuickstartTheme, 'thankYouGameLogoHeight' | 'thankYouStudioLogoHeight'>,
): IconDropLogoSizes => {
  const gameHeight = theme.thankYouGameLogoHeight ?? ICON_DROP_DEFAULT_GAME_LOGO_PX;
  const estimatedGameWidthPx = estimateGameLogoWidthPx(gameHeight);
  const containBox = resolveStudioContainBox(estimatedGameWidthPx, gameHeight);

  return {
    studioHeight: resolveStudioHeightCap(gameHeight, theme.thankYouStudioLogoHeight),
    gameHeight,
    studioMaxWidthFraction: containBox.maxWidth / ICON_DROP_ARTBOARD_WIDTH,
    gameMaxWidthFraction: ICON_DROP_PX.gameLogoMaxWidth,
  };
};

export const resolveIconDropLogoHeights = (
  theme: Pick<QuickstartTheme, 'thankYouGameLogoHeight' | 'thankYouStudioLogoHeight'>,
): { studio: number; game: number } => {
  const sizes = resolveIconDropLogoSizes(theme);
  return { studio: sizes.studioHeight, game: sizes.gameHeight };
};
